<template>
  <div class="skins-megamenu-wrapper" ref="wrapperRef">
    <!-- Trigger Button -->
    <button
      type="button"
      class="skins-trigger-btn hidden md:inline-flex"
      :class="{
        'skins-trigger-btn--open': isOpen,
      }"
      @click="toggleDropdown"
      aria-haspopup="true"
      :aria-expanded="isOpen"
    >
      <i class="fa-solid fa-layer-group text-xs text-[#386f99]"></i>
      <span class="skins-trigger-label">Skins</span>
      <span class="skins-active-tag truncate max-w-[120px]">
        {{ activeSkinDisplay }}
      </span>
      <i
        class="fa-solid fa-chevron-down skins-trigger-chevron"
        :class="{ 'rotate-180': isOpen }"
      ></i>
    </button>

    <!-- Backdrop Overlay -->
    <div
      v-if="isOpen"
      class="megamenu-backdrop"
      :class="
        isMenuOpenSidebar ? 'megamenu-sidebar-open' : 'megamenu-sidebar-closed'
      "
      @click="closeDropdown"
    ></div>

    <!-- Mega-Menu Full-Width Dropdown Panel (Compact & Searchable) -->
    <transition name="megamenu-slide">
      <div
        v-if="isOpen"
        class="megamenu-panel shadow-2xl"
        :class="
          isMenuOpenSidebar
            ? 'megamenu-sidebar-open'
            : 'megamenu-sidebar-closed'
        "
        role="menu"
        @click.stop
      >
        <div class="megamenu-container">
          <!-- Top Header Bar: Title, Count, Search & All Skins Button -->
          <div class="megamenu-topbar">
            <!-- Left: Icon & Title -->
            <div class="flex items-center gap-3">
              <div class="topbar-icon-box">
                <i class="fa-solid fa-layer-group"></i>
              </div>
              <div>
                <div class="flex items-center gap-2">
                  <span class="topbar-title">Available Operator Skins</span>
                  <span class="topbar-count-badge">{{
                    filteredSkins.length
                  }}</span>
                </div>
                <div class="topbar-subtitle hidden sm:block">
                  Filter and select any casino operator skin environment
                </div>
              </div>
            </div>

            <!-- Right: Search Bar & All Skins Action -->
            <div
              class="search-box-wrapper flex items-center gap-2 w-full sm:w-auto"
            >
              <div class="relative flex-1 sm:w-72">
                <i
                  class="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs pointer-events-none"
                ></i>
                <input
                  type="text"
                  v-model="searchQuery"
                  ref="searchInputRef"
                  placeholder="Search by name or ref ID..."
                  class="search-input"
                />
                <button
                  v-if="searchQuery"
                  @click="searchQuery = ''"
                  class="search-clear-btn"
                  title="Clear search"
                  type="button"
                >
                  <i class="fa-solid fa-xmark text-xs"></i>
                </button>
              </div>

              <button
                v-if="isAdmin"
                @click="clearSkinFilt"
                class="all-skins-btn whitespace-nowrap"
                :class="{
                  'all-skins-btn--active': !skinFilt,
                }"
                :disabled="!skinFilt"
                title="View all skins"
                type="button"
              >
                <span>All Skins</span>
              </button>
            </div>
          </div>

          <!-- A-Z Alphabet Quick Filter Bar (When not searching) -->
          <div v-if="!searchQuery" class="alphabet-filter-bar">
            <button
              type="button"
              class="az-pill"
              :class="{ 'az-pill--active': selectedLetter === 'ALL' }"
              @click="selectedLetter = 'ALL'"
            >
              ALL
            </button>
            <button
              v-for="letter in availableAlphabet"
              :key="letter"
              type="button"
              class="az-pill"
              :class="{
                'az-pill--active': selectedLetter === letter,
                'az-pill--disabled':
                  !groupedSkins[letter] || groupedSkins[letter].length === 0,
              }"
              :disabled="
                !groupedSkins[letter] || groupedSkins[letter].length === 0
              "
              @click="selectLetter(letter)"
            >
              <span>{{ letter }}</span>
              <span v-if="groupedSkins[letter]" class="az-count-dot"></span>
            </button>
          </div>

          <!-- Main Scrollable Content Area -->
          <div class="skins-content-scrollable">
            <!-- No Results State -->
            <div v-if="filteredSkins.length === 0" class="no-results-state">
              <div class="no-results-icon">
                <i class="fa-solid fa-magnifying-glass"></i>
              </div>
              <p class="no-results-title">
                No skins found matching "<span class="text-slate-800">{{
                  searchQuery
                }}</span
                >"
              </p>
              <button
                type="button"
                class="reset-search-btn"
                @click="
                  searchQuery = '';
                  selectedLetter = 'ALL';
                "
              >
                Reset search
              </button>
            </div>

            <!-- Single Smooth Flow of Grouped Skins -->
            <div v-else class="skins-sections-flow">
              <div
                v-for="(skinsInGroup, letter) in displayGroupedSkins"
                :key="letter"
                class="letter-section"
              >
                <!-- Compact Letter Section Divider -->
                <div class="letter-section-header">
                  <div class="letter-badge">
                    <span>{{ letter }}</span>
                  </div>
                  <span class="letter-section-count">
                    {{ skinsInGroup.length }}
                    {{ skinsInGroup.length === 1 ? "skin" : "skins" }}
                  </span>
                  <div class="letter-section-line"></div>
                </div>

                <!-- Dense 4-Column Responsive Grid of Skin Chips -->
                <div class="skins-grid">
                  <div
                    v-for="skin in skinsInGroup"
                    :key="skin.id || skin.ref_id"
                    class="skin-chip"
                    :class="{
                      'skin-chip--active':
                        skin?.ref_id?.toString() === skinFilt,
                    }"
                    @click="selectSkin(skin)"
                    :title="`${skin.name} (ID: ${skin.ref_id})`"
                  >
                    <div class="skin-chip-left">
                      <span
                        class="skin-chip-name"
                        v-html="highlightMatch(skin.name)"
                      ></span>
                    </div>

                    <div class="skin-chip-right">
                      <span
                        class="skin-chip-id"
                        v-html="highlightMatch(String(skin.ref_id))"
                      ></span>
                      <i
                        v-if="skin?.ref_id?.toString() === skinFilt"
                        class="fa-solid fa-circle-check skin-chip-check"
                      ></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { getFetch } from "@/api/axiosClient";
import { useHeaderNavigation } from "@/composables/useHeaderNavigation";

const store = useStore();
const route = useRoute();
const router = useRouter();

const { isMenuOpen, toggleMenu, closeMenu, openMenu } = useHeaderNavigation();
const wrapperRef = ref(null);
const searchInputRef = ref(null);
const searchQuery = ref("");
const selectedLetter = ref("ALL");

const isOpen = computed(() => isMenuOpen("skins"));
const isMenuOpenSidebar = computed(() => Boolean(store?.state?.isMenuOpen));

const allowedSkins = computed(() => store.getters.allowedSkins || []);
const isAdmin = computed(() => store.getters.isAdmin);

const skinFilt = computed(() => {
  return route.params.skinFilt ? route.params.skinFilt.toString() : "";
});

const activeSkinDisplay = computed(() => {
  if (!skinFilt.value) {
    return isAdmin.value ? "All" : "Default";
  }
  const selected = allowedSkins.value.find(
    (s) => s.ref_id?.toString() === skinFilt.value,
  );
  return selected ? selected.ref_id : skinFilt.value;
});

const availableAlphabet = [
  "#",
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
];

const filteredSkins = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return allowedSkins.value;

  return allowedSkins.value.filter((skin) => {
    const nameMatch = skin.name && skin.name.toLowerCase().includes(query);
    const idMatch = skin.ref_id && skin.ref_id.toString().includes(query);
    return nameMatch || idMatch;
  });
});

/**
 * Group filtered skins alphabetically
 */
const groupedSkins = computed(() => {
  const sorted = [...filteredSkins.value].sort((a, b) => {
    const nameA = (a.name || "").trim().toLowerCase();
    const nameB = (b.name || "").trim().toLowerCase();
    return nameA.localeCompare(nameB);
  });

  const groups = {};

  sorted.forEach((skin) => {
    const firstChar = (skin.name || "").trim().charAt(0).toUpperCase();
    const letterKey = /^[A-Z]$/.test(firstChar) ? firstChar : "#";

    if (!groups[letterKey]) {
      groups[letterKey] = [];
    }
    groups[letterKey].push(skin);
  });

  return groups;
});

/**
 * Filtered by active letter selector tab
 */
const displayGroupedSkins = computed(() => {
  if (searchQuery.value.trim()) {
    return groupedSkins.value;
  }
  if (selectedLetter.value === "ALL") {
    return groupedSkins.value;
  }
  if (groupedSkins.value[selectedLetter.value]) {
    return {
      [selectedLetter.value]: groupedSkins.value[selectedLetter.value],
    };
  }
  return {};
});

const selectLetter = (letter) => {
  selectedLetter.value = letter;
};

const toggleDropdown = () => {
  toggleMenu("skins");
  // if (isOpen.value) {
  //   nextTick(() => {
  //     if (searchInputRef.value) searchInputRef.value.focus();
  //   });
  // }
};

const closeDropdown = () => {
  if (isOpen.value) {
    closeMenu();
    searchQuery.value = "";
    selectedLetter.value = "ALL";
  }
};

const openModal = () => {
  setTimeout(() => {
    openMenu("skins");
    nextTick(() => {
      if (searchInputRef.value) searchInputRef.value.focus();
    });
  }, 20);
};

const highlightMatch = (text) => {
  if (!text) return "";
  const query = searchQuery.value.trim();
  if (!query) return text;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escaped})`, "gi");
  return text.replace(regex, `<mark class="search-highlight">$1</mark>`);
};

const clearSkinFilt = () => {
  store.commit("setSkinID", "");
  closeDropdown();
  const currentLang =
    route.params.lang || localStorage.getItem("locale") || "en";

  if (
    route.name &&
    route.name !== "NotFound" &&
    route.name !== "NotFoundPage"
  ) {
    try {
      const resolved = router.resolve({
        name: route.name,
        params: { ...route.params, lang: currentLang, skinFilt: undefined },
        query: route.query,
      });
      if (resolved && resolved.href) {
        if (
          window.location.pathname + window.location.search ===
          resolved.href
        ) {
          window.location.reload();
        } else {
          window.location.href = resolved.href;
        }
        return;
      }
    } catch (e) {
      console.warn("Could not resolve route without skin:", e);
    }
  }

  const pathSegments = window.location.pathname.split("/").filter(Boolean);
  if (pathSegments.length > 1 && /^\d+$/.test(pathSegments[1])) {
    pathSegments.splice(1, 1);
  }
  const targetUrl = "/" + pathSegments.join("/") + window.location.search;
  if (window.location.pathname + window.location.search === targetUrl) {
    window.location.reload();
  } else {
    window.location.href = targetUrl;
  }
};

const selectSkin = (skin) => {
  const skinId = skin.ref_id;
  store.commit("setSkinID", skinId);
  closeDropdown();
  const currentLang =
    route.params.lang || localStorage.getItem("locale") || "en";

  if (
    route.name &&
    route.name !== "NotFound" &&
    route.name !== "NotFoundPage"
  ) {
    try {
      const resolved = router.resolve({
        name: route.name,
        params: {
          ...route.params,
          lang: currentLang,
          skinFilt: skinId.toString(),
        },
        query: route.query,
      });
      if (resolved && resolved.href) {
        if (
          window.location.pathname + window.location.search ===
          resolved.href
        ) {
          window.location.reload();
        } else {
          window.location.href = resolved.href;
        }
        return;
      }
    } catch (e) {
      console.warn("Could not resolve route with new skin:", e);
    }
  }

  const pathSegments = window.location.pathname.split("/").filter(Boolean);
  if (pathSegments.length > 0) {
    pathSegments[0] = currentLang;
    if (pathSegments.length > 1 && /^\d+$/.test(pathSegments[1])) {
      pathSegments[1] = skinId.toString();
    } else {
      pathSegments.splice(1, 0, skinId.toString());
    }
  } else {
    pathSegments.push(currentLang, skinId.toString());
  }
  const targetUrl = "/" + pathSegments.join("/") + window.location.search;
  if (window.location.pathname + window.location.search === targetUrl) {
    window.location.reload();
  } else {
    window.location.href = targetUrl;
  }
};

const handleClickOutside = (event) => {
  if (!isOpen.value) return;
  if (wrapperRef.value && !wrapperRef.value.contains(event.target)) {
    closeDropdown();
  }
};

const handleKeyDown = (event) => {
  if (event.key === "Escape" && isOpen.value) {
    closeDropdown();
  }
};

defineExpose({
  openModal,
  openMenu: openModal,
});

// Body Class management for custom CSS & operator customization
watch(
  isOpen,
  (val) => {
    if (typeof document !== "undefined") {
      if (val) {
        document.body.classList.add("skins-megamenu-open");
        document.body.classList.add("megamenu-open");
      } else {
        document.body.classList.remove("skins-megamenu-open");
        document.body.classList.remove("megamenu-open");
      }
    }
  },
  { immediate: true },
);

onMounted(async () => {
  document.addEventListener("click", handleClickOutside);
  document.addEventListener("keydown", handleKeyDown);

  if (!allowedSkins.value || allowedSkins.value.length === 0) {
    try {
      const res = await getFetch(`/skins`, {}, false);
      if (res && res.data) {
        store.commit("setAllowedSkins", res.data);
      }
    } catch (err) {
      console.error("Error loading skins for mega-menu:", err);
    }
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
  document.removeEventListener("keydown", handleKeyDown);
  if (typeof document !== "undefined") {
    document.body.classList.remove("skins-megamenu-open");
    document.body.classList.remove("megamenu-open");
  }
});
</script>

<style lang="scss" scoped>
.skins-megamenu-wrapper {
  position: static;
  display: inline-flex;
  align-items: stretch;

  @media (max-width: 768px) {
    display: contents;
  }
}

/* Trigger Button */
.skins-trigger-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.95rem;
  border-radius: 9999px;
  background: #f8fafc;
  color: #1e293b;
  font-weight: 700;
  font-size: 0.875rem;
  border: 1px solid #cbd5e1;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  user-select: none;
  flex-grow: 1;

  @media (max-width: 768px) {
    display: none !important;
  }

  .skins-trigger-label {
    color: inherit;
  }

  .skins-active-tag {
    font-size: 0.65rem;
    font-weight: 800;
    padding: 1px 6px;
    border-radius: 5px;
    background: #e2e8f0;
    color: #475569;
    letter-spacing: 0.03em;
  }

  .skins-trigger-chevron {
    font-size: 0.7rem;
    transition: transform 0.25s ease;
    color: inherit;
  }

  &:hover {
    background: #eef6fa;
    color: var(--primary-color, #386f99);
    border-color: var(--primary-color, #386f99);
    box-shadow: 0 4px 14px rgba(56, 111, 153, 0.15);

    .skins-active-tag {
      background: rgba(56, 111, 153, 0.15);
      color: var(--primary-color, #386f99);
    }
  }

  &--open {
    background: #eef6fa;
    color: var(--primary-color, #386f99);
    border-color: var(--primary-color, #386f99);
    box-shadow: 0 0 0 3px rgba(56, 111, 153, 0.2);

    .skins-active-tag {
      background: var(--primary-color, #386f99);
      color: #ffffff;
    }
  }
}

/* Backdrop Overlay (Frosted Apple Glass) */
.megamenu-backdrop {
  position: fixed;
  top: 80px;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.32);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 99998;
  transition: left 0.25s cubic-bezier(0.16, 1, 0.3, 1),
    width 0.25s cubic-bezier(0.16, 1, 0.3, 1);

  &.megamenu-sidebar-open {
    left: 250px;
    width: calc(100% - 250px);
    @media (max-width: 768px) {
      left: 0;
      width: 100%;
    }
  }

  &.megamenu-sidebar-closed {
    left: 64px;
    width: calc(100% - 64px);
    @media (max-width: 768px) {
      left: 0;
      width: 100%;
    }
  }
}

/* Mega-Menu Panel (Apple Style with Distinct Colored Top Border) */
.megamenu-panel {
  position: fixed;
  top: 80px;
  right: 0;
  background: #ffffff;
  // border-top: 3px solid var(--primary-color, #386f99);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  padding: 1.15rem 0 1.5rem 0;
  z-index: 99999;
  box-shadow: 0 24px 48px -12px rgba(15, 23, 42, 0.12),
    0 4px 16px rgba(15, 23, 42, 0.04);
  max-height: calc(100dvh - 86px);
  overflow-y: auto;
  transition: left 0.25s cubic-bezier(0.16, 1, 0.3, 1),
    width 0.25s cubic-bezier(0.16, 1, 0.3, 1);

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f8fafc;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
    &:hover {
      background: #94a3b8;
    }
  }

  &.megamenu-sidebar-open {
    left: 250px;
    width: calc(100% - 250px);
    @media (max-width: 768px) {
      left: 0;
      width: 100%;
    }
  }

  &.megamenu-sidebar-closed {
    left: 64px;
    width: calc(100% - 64px);
    @media (max-width: 768px) {
      left: 0;
      width: 100%;
    }
  }
}

.megamenu-container {
  max-width: 1800px;
  margin: 0 auto;
  padding: 0 1.75rem;

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
}

/* Topbar */
.megamenu-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 0.85rem;
  margin-bottom: 0.85rem;
  border-bottom: 1px solid #f1f5f9;
  flex-wrap: wrap;

  .topbar-icon-box {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: #f5f5f7;
    border: 1px solid rgba(0, 0, 0, 0.05);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-color, #386f99);
    font-size: 0.85rem;
  }

  .topbar-title {
    font-size: 0.95rem;
    font-weight: 700;
    color: #1d1d1f;
    letter-spacing: -0.015em;
  }

  .topbar-subtitle {
    font-size: 0.725rem;
    color: #86868b;
    font-weight: 500;
    margin-top: 1px;
  }

  .topbar-count-badge {
    font-size: 0.7rem;
    font-weight: 700;
    padding: 2px 7px;
    border-radius: 9999px;
    background: #f5f5f7;
    color: #6e6e73;
    border: 1px solid rgba(0, 0, 0, 0.04);
  }
}

/* Apple Spotlight Style Search Box */
.search-box-wrapper {
  .search-input {
    width: 100%;
    padding: 0.42rem 2rem 0.42rem 2.2rem;
    background: #f5f5f7;
    border: 1px solid transparent;
    border-radius: 9999px;
    font-size: 0.8rem;
    font-weight: 500;
    color: #1d1d1f;
    outline: none;
    transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);

    &::placeholder {
      color: #86868b;
    }

    &:focus {
      background: #ffffff;
      border-color: var(--primary-color, #386f99);
      box-shadow: 0 0 0 3px rgba(56, 111, 153, 0.14);
    }
  }

  .search-clear-btn {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #86868b;
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: #1d1d1f;
    }
  }
}

.all-skins-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.35rem 0.85rem;
  border-radius: 9999px;
  background: #f5f5f7;
  border: 1px solid transparent;
  color: #6e6e73;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover:not(:disabled) {
    background: #e8e8ed;
    color: #1d1d1f;
  }

  &--active {
    background: #1d1d1f !important;
    border-color: transparent !important;
    color: #ffffff !important;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);

    &:hover:not(:disabled) {
      background: #2d2d30 !important;
      color: #ffffff !important;
    }
  }

  &:disabled {
    opacity: 0.85;
    cursor: default;
  }
}

/* Alphabet Quick Filter Bar */
.alphabet-filter-bar {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.35rem 0.5rem;
  margin-bottom: 0.75rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow-x: auto;

  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }

  .az-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 26px;
    height: 24px;
    padding: 0 0.4rem;
    border-radius: 6px;
    font-size: 0.7rem;
    font-weight: 700;
    color: #64748b;
    background: transparent;
    border: 1px solid transparent;
    cursor: pointer;
    transition: all 0.15s ease;
    flex-shrink: 0;
    position: relative;

    &:hover:not(.az-pill--disabled) {
      background: #eef6fa;
      color: var(--primary-color, #386f99);
    }

    &--active {
      background: var(--primary-color, #386f99) !important;
      color: #ffffff !important;
      box-shadow: 0 1px 4px rgba(56, 111, 153, 0.25);
    }

    &--disabled {
      opacity: 0.35;
      cursor: not-allowed;
    }
  }

  .az-count-dot {
    position: absolute;
    bottom: 2px;
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.7;
  }
}

/* Scrollable Continuous Skins Container */
.skins-content-scrollable {
  max-height: calc(65vh - 130px);
  min-height: 140px;
  overflow-y: auto;
  padding-right: 0.35rem;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 6px;
    &:hover {
      background: #94a3b8;
    }
  }
}

/* No Results State */
.no-results-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
  text-align: center;

  .no-results-icon {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: #f1f5f9;
    color: #94a3b8;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    margin-bottom: 0.75rem;
  }

  .no-results-title {
    font-size: 0.85rem;
    color: #64748b;
    margin-bottom: 0.75rem;
    font-weight: 600;
  }

  .reset-search-btn {
    padding: 0.35rem 0.85rem;
    border-radius: 6px;
    background: #eef6fa;
    color: var(--primary-color, #386f99);
    border: 1px solid #d1e3ef;
    font-size: 0.75rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: var(--primary-color, #386f99);
      color: #ffffff;
    }
  }
}

/* Continuous Section Flow */
.skins-sections-flow {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.letter-section {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

/* Letter Section Header */
.letter-section-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.15rem;

  .letter-badge {
    width: 22px;
    height: 22px;
    border-radius: 6px;
    background: #eef6fa;
    border: 1px solid #d1e3ef;
    color: var(--primary-color, #386f99);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 800;
    flex-shrink: 0;
  }

  .letter-section-count {
    font-size: 0.65rem;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .letter-section-line {
    flex: 1;
    height: 1px;
    background: #f1f5f9;
  }
}

/* Dense 4-Column Responsive Grid */
.skins-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.4rem;

  @media (max-width: 1150px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 800px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
}

/* Compact Skin Chip */
.skin-chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.4rem;
  padding: 0.32rem 0.6rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s cubic-bezier(0.16, 1, 0.3, 1);
  user-select: none;
  min-height: 30px;

  &:hover {
    background: #ffffff;
    border-color: #cbd5e1;
    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.06);
    transform: translateY(-1px);

    .skin-chip-name {
      color: var(--primary-color, #386f99);
    }
  }

  &--active {
    background: #e6f1f8;
    border-color: var(--primary-color, #386f99);
    box-shadow: 0 1px 4px rgba(56, 111, 153, 0.15);

    .skin-chip-name {
      color: var(--primary-color, #386f99);
      font-weight: 800;
    }

    .skin-chip-id {
      background: var(--primary-color, #386f99);
      color: #ffffff;
    }
  }
}

.skin-chip-left {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
  flex: 1;

  .skin-chip-name {
    font-size: 0.78rem;
    font-weight: 700;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.15;
  }
}

.skin-chip-right {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  flex-shrink: 0;

  .skin-chip-id {
    font-size: 0.62rem;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
      monospace;
    font-weight: 700;
    padding: 1px 4px;
    border-radius: 4px;
    background: #e2e8f0;
    color: #475569;
    letter-spacing: -0.01em;
    transition: all 0.15s ease;
  }

  .skin-chip-check {
    color: var(--primary-color, #386f99);
    font-size: 0.75rem;
  }
}

:deep(.search-highlight) {
  background: #fef08a;
  color: #854d0e;
  font-weight: 800;
  border-radius: 2px;
  padding: 0 1px;
}

/* Transitions */
.megamenu-slide-enter-active {
  transition: all 0.22s cubic-bezier(0.16, 1, 0.3, 1);
}

.megamenu-slide-leave-active {
  transition: all 0.16s cubic-bezier(0.4, 0, 1, 1);
}

.megamenu-slide-enter-from {
  opacity: 0;
  transform: translateY(-6px);
}

.megamenu-slide-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
